import { writable, derived } from 'svelte/store';
import { nl } from './translations/nl.js';
import { en } from './translations/en.js';

export type Locale = 'nl' | 'en';

const translations: Record<Locale, Record<string, unknown>> = { nl, en };

export const locale = writable<Locale>('en');

function lookup(obj: Record<string, unknown>, path: string): string | undefined {
  const parts = path.split('.');
  let cur: unknown = obj;
  for (const p of parts) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return typeof cur === 'string' ? cur : undefined;
}

export const t = derived(locale, ($locale) => {
  const strings = translations[$locale];
  return (key: string, params?: Record<string, string | number>): string => {
    let str = lookup(strings, key) ?? key;
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        str = str.replaceAll(`{${k}}`, String(v));
      }
    }
    return str;
  };
});

export function setLocale(l: Locale) {
  locale.set(l);
  try { localStorage.setItem('timelock-lang', l); } catch { /* private browsing */ }
  document.documentElement.lang = l;
  const tf = translations[l];
  document.title = lookup(tf, 'meta.title') ?? 'TimeLock';
  const desc = document.querySelector('meta[name="description"]');
  if (desc) desc.setAttribute('content', lookup(tf, 'meta.description') ?? '');
}

export function initLocale() {
  const params = new URLSearchParams(location.search);
  const fromUrl = params.get('lang') as Locale | null;
  if (fromUrl && translations[fromUrl]) {
    setLocale(fromUrl);
    return;
  }
  try {
    const stored = localStorage.getItem('timelock-lang') as Locale | null;
    if (stored && translations[stored]) { setLocale(stored); return; }
  } catch { /* private browsing */ }
  const browserLang = navigator.language.startsWith('nl') ? 'nl' : 'en';
  setLocale(browserLang);
}
